// v2 ramp — a modern freight dock: a raised concrete platform on a darker plinth
// with a yellow-and-black hazard edge, a wide sloped vehicle apron with kerb
// cheeks running down to the road side (+z), an A-frame gantry crane spanning the
// dock with a trolley, cable and a crate swinging on the hook, palletised cargo
// stacks, and bumper bollards flanking the apron foot. Footprint-native 2x2.
// Ported 1:1 from age-of-mcp's v2/ramp.ts, using the library's shared
// `foundationSlab`, `scaffold`, `barrel` and `estate` (cargoCrate, bollard)
// helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, barrel, estate, type Part } from "../../lib";

const CONCRETE = "#8d8d92";   // dock platform
const PLINTH   = "#6e6e74";   // darker base course
const APRON    = "#55555c";   // sloped vehicle apron
const KERB     = "#9c9ca2";   // apron kerb cheeks
const HAZARD   = "#d8b13a";   // hazard edge strip
const CHIP     = "#2e2e30";   // hazard chevrons
const STEEL    = "#4a4f58";   // crane frame
const PALLET   = "#b09a6a";   // pallet slats
const FORM     = "#7a5a30";   // stage-1 shuttering planks

const BASE     = 0.14;        // foundation top
const DOCK_TOP = 0.52;        // platform top
const DOCK_D   = 1.2;         // platform depth (z -0.92 .. 0.28)
const DOCK_CZ  = -0.32;       // platform centre z
const FRONT_Z  = DOCK_CZ + DOCK_D / 2;   // 0.28 — the apron springs here

// The raised concrete platform on its darker plinth course.
function dock(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(1.9, 0.12, 1.24), color: PLINTH,
      position: [0, BASE + 0.06, DOCK_CZ] },
    { geometry: new THREE.BoxGeometry(1.86, DOCK_TOP - BASE - 0.1, 1.2), color: CONCRETE,
      position: [0, (DOCK_TOP + BASE + 0.1) / 2, DOCK_CZ] },
  ];
}

// The yellow-and-black hazard strip along the platform's loading edge.
function hazardEdge(): Part[] {
  const out: Part[] = [
    { geometry: new THREE.BoxGeometry(1.86, 0.015, 0.09), color: HAZARD,
      position: [0, DOCK_TOP + 0.0075, FRONT_Z - 0.045] },
  ];
  for (const x of [-0.62, 0, 0.62]) {
    out.push({ geometry: new THREE.BoxGeometry(0.14, 0.018, 0.092), color: CHIP,
      position: [x, DOCK_TOP + 0.011, FRONT_Z - 0.045] });
  }
  return out;
}

// The sloped vehicle apron from the platform edge down to the road side, with
// a kerb cheek along each side. Rotation +theta about X drops the +z (road) end.
function apron(): Part[] {
  const y0 = DOCK_TOP - 0.02, y1 = BASE + 0.02;
  const z0 = FRONT_Z, z1 = 0.93;
  const rise = y0 - y1, run = z1 - z0;
  const ang = Math.atan2(rise, run);
  const len = Math.hypot(rise, run);
  const mid: [number, number, number] = [0, (y0 + y1) / 2, (z0 + z1) / 2];
  return [
    { geometry: new THREE.BoxGeometry(1.5, 0.05, len), color: APRON,
      position: mid, rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.07, 0.11, len), color: KERB,
      position: [-0.72, mid[1] + 0.03, mid[2]], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.07, 0.11, len), color: KERB,
      position: [0.72, mid[1] + 0.03, mid[2]], rotation: [ang, 0, 0] },
  ];
}

// The A-frame gantry crane: two pairs of leaning legs carrying a beam, with a
// trolley, cable, hook and a crate mid-hoist over the platform.
function crane(): Part[] {
  const apexY = 1.16;
  const legLen = 0.72, legLean = 0.438;   // atan2(0.30, 0.64)
  const out: Part[] = [];
  for (const x of [-0.74, 0.74]) {
    // +theta about X tips a leg's top toward +z, so the +z-footed leg gets -theta.
    out.push(
      { geometry: new THREE.BoxGeometry(0.07, legLen, 0.07), color: STEEL,
        position: [x, (DOCK_TOP + apexY) / 2, 0.15], rotation: [-legLean, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.07, legLen, 0.07), color: STEEL,
        position: [x, (DOCK_TOP + apexY) / 2, -0.15], rotation: [legLean, 0, 0] },
      { geometry: new THREE.BoxGeometry(0.12, 0.1, 0.14), color: STEEL,
        position: [x, apexY + 0.02, 0] },
    );
  }
  out.push(
    { geometry: new THREE.BoxGeometry(1.66, 0.09, 0.09), color: STEEL,
      position: [0, apexY + 0.045, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.07, 0.13), color: CHIP,
      position: [0.25, apexY - 0.025, 0] },
    { geometry: new THREE.BoxGeometry(0.018, 0.3, 0.018), color: "#222",
      position: [0.25, 1.0, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.07, 0.05), color: CHIP,
      position: [0.25, 0.82, 0] },
    ...estate.cargoCrate(0.25, 0.56, 0, 0.22),
  );
  return out;
}

// Palletised cargo stacked on the platform, ready to ship.
function cargo(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.36, 0.04, 0.36), color: PALLET,
      position: [-0.55, DOCK_TOP + 0.02, -0.5] },
    ...estate.cargoCrate(-0.55, DOCK_TOP + 0.04, -0.5, 0.26),
    ...estate.cargoCrate(-0.55, DOCK_TOP + 0.3, -0.5, 0.18),
    { geometry: new THREE.BoxGeometry(0.36, 0.04, 0.36), color: PALLET,
      position: [-0.14, DOCK_TOP + 0.02, -0.72] },
    barrel(-0.14, -0.72, DOCK_TOP + 0.04, 0.26, 0.11),
    ...estate.cargoCrate(0.52, DOCK_TOP, -0.78, 0.2),
  ];
}

// Stage-1 shuttering: formwork planks outlining the platform pour.
function formwork(): Part[] {
  const h = 0.22, y = BASE + h / 2;
  return [
    { geometry: new THREE.BoxGeometry(1.9, h, 0.05), color: FORM, position: [0, y, DOCK_CZ - 0.62] },
    { geometry: new THREE.BoxGeometry(1.9, h, 0.05), color: FORM, position: [0, y, DOCK_CZ + 0.62] },
    { geometry: new THREE.BoxGeometry(0.05, h, 1.28), color: FORM, position: [-0.94, y, DOCK_CZ] },
    { geometry: new THREE.BoxGeometry(0.05, h, 1.28), color: FORM, position: [0.94, y, DOCK_CZ] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), ...formwork(), ...scaffold(1.7, 0.55)];
  if (s === 2) return [foundationSlab(1.94), ...dock(), ...scaffold(1.7, 0.95)];
  return [
    foundationSlab(1.94),
    ...dock(),
    ...hazardEdge(),
    ...apron(),
    ...crane(),
    ...cargo(),
    ...estate.bollard(-0.84, BASE, 0.86),
    ...estate.bollard(0.84, BASE, 0.86),
  ];
}

export const rampV2 = defineModel({
  id: "ramp-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Ramp (v2)",
  tags: ["building","ramp","low-poly","age-of-mcp","fable"],
  summary: "Low-poly ramp.",
  description: "A raised freight dock.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [],
});
